/**
 * SLA Utility Functions
 * Provides calculations for Service Level Agreement tracking
 */

/**
 * Calculate SLA information for a ticket
 * @param {Object} ticket - The ticket object
 * @returns {Object} SLA information including response/resolution times and statuses
 */
export const calculateSLAInfo = (ticket) => {
  if (!ticket?.created_at || !ticket?.priority) {
    return {
      response_time: '--',
      resolution_time: '--',
      response_status: 'pending',
      resolution_status: 'pending',
      hoursPassed: 0,
      responseTarget: 0,
      resolutionTarget: 0
    };
  }

  const priority = ticket.priority.toLowerCase();
  const created = new Date(ticket.created_at);
  const now = new Date();
  const hoursPassed = Math.floor((now - created) / (1000 * 60 * 60));
  
  // SLA targets based on priority (in hours)
  const slaTargets = {
    critical: { response: 1, resolution: 4 },
    urgent: { response: 1, resolution: 4 },
    high: { response: 4, resolution: 24 },
    medium: { response: 8, resolution: 72 },
    low: { response: 24, resolution: 168 }
  };
  
  const target = slaTargets[priority] || slaTargets.medium;
  
  // Calculate response status
  let responseStatus = 'pending';
  if (ticket.status === 'closed' || ticket.status === 'resolved' || ticket.status === 'completed') {
    responseStatus = hoursPassed <= target.response ? 'on_track' : 'overdue';
  } else if (hoursPassed > target.response) {
    responseStatus = 'overdue';
  } else if (hoursPassed > target.response * 0.8) {
    responseStatus = 'due_soon';
  } else {
    responseStatus = 'on_track';
  }
  
  // Calculate resolution status
  let resolutionStatus = 'pending';
  if (ticket.status === 'closed' || ticket.status === 'resolved' || ticket.status === 'completed') {
    resolutionStatus = hoursPassed <= target.resolution ? 'on_track' : 'overdue';
  } else if (hoursPassed > target.resolution) {
    resolutionStatus = 'overdue';
  } else if (hoursPassed > target.resolution * 0.8) {
    resolutionStatus = 'due_soon';
  } else {
    resolutionStatus = 'on_track';
  }

  return {
    response_time: formatTime(hoursPassed, target.response),
    resolution_time: formatTime(hoursPassed, target.resolution),
    response_status: responseStatus,
    resolution_status: resolutionStatus,
    hoursPassed,
    responseTarget: target.response,
    resolutionTarget: target.resolution,
    responsePercentage: Math.min((hoursPassed / target.response) * 100, 100),
    resolutionPercentage: Math.min((hoursPassed / target.resolution) * 100, 100)
  };
};

/**
 * Format time display for SLA tracking
 * @param {number} hours - Hours passed
 * @param {number} targetHours - Target hours for SLA
 * @returns {string} Formatted time string
 */
export const formatTime = (hours, targetHours) => {
  if (hours < 1) {
    const minutes = Math.floor(hours * 60);
    return `${minutes}m / ${targetHours}h`;
  } else if (hours < 24) {
    return `${Math.floor(hours)}h / ${targetHours}h`;
  } else {
    const days = Math.floor(hours / 24);
    const remainingHours = Math.floor(hours % 24);
    return `${days}d ${remainingHours}h / ${targetHours}h`;
  }
};

/**
 * Get SLA status color for UI display
 * @param {string} status - SLA status (on_track, due_soon, overdue)
 * @returns {string} CSS color class or hex color
 */
export const getSLAStatusColor = (status) => {
  switch (status) {
    case 'on_track': return '#10b981'; // green
    case 'due_soon': return '#f59e0b'; // amber
    case 'overdue': return '#ef4444'; // red
    default: return '#6b7280'; // gray
  }
};

/**
 * Get SLA status text for display
 * @param {string} status - SLA status
 * @returns {string} Human-readable status text
 */
export const getSLAStatusText = (status) => {
  switch (status) {
    case 'on_track': return 'On Track';
    case 'due_soon': return 'Due Soon';
    case 'overdue': return 'Overdue';
    default: return 'Pending';
  }
};

/**
 * Check if a ticket has breached SLA
 * @param {Object} ticket - The ticket object
 * @returns {Object} Breach information
 */
export const checkSLABreach = (ticket) => {
  const slaInfo = calculateSLAInfo(ticket);
  
  return {
    responseBreached: slaInfo.response_status === 'overdue',
    resolutionBreached: slaInfo.resolution_status === 'overdue',
    anyBreached: slaInfo.response_status === 'overdue' || slaInfo.resolution_status === 'overdue',
    dueSoon: slaInfo.response_status === 'due_soon' || slaInfo.resolution_status === 'due_soon'
  };
};

/**
 * Add SLA information to a ticket object
 * @param {Object} ticket - The ticket object
 * @returns {Object} Ticket with SLA information added
 */
export const enrichTicketWithSLA = (ticket) => {
  const slaInfo = calculateSLAInfo(ticket);
  
  return {
    ...ticket,
    response_time: slaInfo.response_time,
    resolution_time: slaInfo.resolution_time,
    response_status: slaInfo.response_status,
    resolution_status: slaInfo.resolution_status,
    sla_info: slaInfo
  };
};

/**
 * Get business hours adjusted SLA calculation
 * This is a placeholder for future business hours logic
 * @param {Object} ticket - The ticket object
 * @returns {Object} Business hours adjusted SLA info
 */
export const calculateBusinessHoursSLA = (ticket) => {
  // For now, return standard calculation
  // In the future, this can include business hours logic
  return calculateSLAInfo(ticket);
};
